import { createSelector } from 'reselect';
import StarTitle from '@atlassian/jira-common-directory-v2/src/components/star-title-cell/view.tsx';
import {
	CHANGE_OWNER,
	COPY,
	EDIT,
	DELETE,
} from '@atlassian/jira-directory-base/src/model/operation-types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { NAME, POPULARITY, FAVOURITED, OWNER } from '../model/constants/sort-field.tsx';
import { getLoggedInUserAccountId } from '../state/selectors/app/index.tsx';
import AccessCell from './content/cells/access/index.tsx';
import ActionsCell from './content/cells/actions/index.tsx';
import EditCell from './content/cells/edit/index.tsx';
import FavoriteCell from './content/cells/favourite/index.tsx';
import OwnerCell from './content/cells/owner/index.tsx';
import PopularityWithActionsCell from './content/cells/popularity-with-actions/index.tsx';
import PopularityCell from './content/cells/popularity/index.tsx';
import TitleCell from './content/cells/title/index.tsx';
import ChangeOwnerModal from './content/change-owner-modal/index.tsx';
import CopyModal from './content/copy-modal/index.tsx';
import DeleteModal from './content/delete-modal/index.tsx';
import EditModal from './content/edit-modal/index.tsx';
import messages from './messages.tsx';

export const operationModals = {
	[COPY]: CopyModal,
	[EDIT]: EditModal,
	[DELETE]: DeleteModal,
	[CHANGE_OWNER]: ChangeOwnerModal,
} as const;

export const emptyFilterResultConfiguration = {
	message: messages.emptyFilterResultMessage,
} as const;

export const getTableConfiguration = createSelector(getLoggedInUserAccountId, (accountId) =>
	[
		accountId !== null
			? {
					title: StarTitle,
					Cell: FavoriteCell,
					sortField: FAVOURITED,
					width: 3,
					isIconOnlyHeader: isVisualRefreshEnabled(),
					ascendingSortTooltip: isVisualRefreshEnabled()
						? messages.starredToUnstarredOrder
						: undefined,
					descendingSortTooltip: isVisualRefreshEnabled()
						? messages.unstarredToStarredOrder
						: undefined,
				}
			: undefined,
		{
			title: messages.name,
			Cell: TitleCell,
			sortField: NAME,
			width: accountId ? 25 : 35,
			ascendingSortTooltip: isVisualRefreshEnabled() ? messages.aToZOrder : undefined,
			descendingSortTooltip: isVisualRefreshEnabled() ? messages.zToAOrder : undefined,
		},
		accountId !== null
			? {
					title: messages.owner,
					Cell: OwnerCell,
					sortField: OWNER,
					width: 19,
					ascendingSortTooltip: isVisualRefreshEnabled() ? messages.aToZOrder : undefined,
					descendingSortTooltip: isVisualRefreshEnabled() ? messages.zToAOrder : undefined,
				}
			: undefined,
		{
			title: messages.viewers,
			Cell: AccessCell,
			// eslint-disable-next-line no-nested-ternary
			width: accountId ? (isVisualRefreshEnabled() ? 19 : 19.5) : 25,
		},
		{
			title: messages.editors,
			Cell: EditCell,
			width: accountId ? 19 : 25,
		},
		{
			title: messages.popularity,
			Cell:
				accountId !== null && isVisualRefreshEnabled() ? PopularityWithActionsCell : PopularityCell,
			sortField: POPULARITY,
			width: accountId !== null && isVisualRefreshEnabled() ? 15 : 9,
			ascendingSortTooltip: isVisualRefreshEnabled() ? messages.lowestToHighestOrder : undefined,
			descendingSortTooltip: isVisualRefreshEnabled() ? messages.highestToLowestOrder : undefined,
		},
		getActionsCell(accountId),
	].filter(Boolean),
);

function getActionsCell(accountId: string | null) {
	if (!accountId) {
		return undefined;
	}

	// If Nav4, meatball menu will be shown in the popularity column
	if (isVisualRefreshEnabled()) {
		return undefined;
	}

	// We still need to show actions column for Nav3
	return {
		title: messages.moreActions,
		Cell: ActionsCell,
		width: 6,
		dataTestId: 'dashboards-main.content.headerCell.actions.container',
	};
}

export const getTableLabel = () => messages.tableAriaLabel;

export const getResultsAnnouncerMessage = () => messages.filterResultsAnnouncement;
